import apiClient, { getApiBaseUrl } from './apiClient';
import { useAuthStore } from '@/stores/authStore';

const apiBaseURL = '/api/cowork';

export type SourceType = 'use_case' | 'standalone' | 'project';
export type PackageStatus = 'pending' | 'generating' | 'ready' | 'failed';

/** A skill or agent shipped in the CoWork library (mirrors `LibraryEntry`). */
export interface LibraryEntry {
  slug: string;
  name: string;
  description: string;
  categories: string[];
  tags: string[];
  source?: string | null;
  license?: string | null;
  path: string;
  files: string[];
  bytes: number;
}

export interface LibraryCatalog {
  version: number;
  skills: LibraryEntry[];
  agents: LibraryEntry[];
}

export interface HandoffPackageFile {
  id: string;
  filename: string;
  content_type?: string | null;
  size_bytes: number;
  created_at: string;
}

/** History-list shape — omits the heavy brief / sub-ontology payloads. */
export interface HandoffPackage {
  id: string;
  organization_id: string;
  user_id?: string | null;
  source_type: SourceType;
  use_case_id?: string | null;
  project_id?: string | null;
  title: string;
  status: PackageStatus;
  selected_skills: string[];
  selected_agents: string[];
  zip_bytes?: number | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
  files: HandoffPackageFile[];
}

export interface HandoffPackageDetail extends HandoffPackage {
  input_text?: string | null;
  brief_markdown?: string | null;
  sub_ontology?: Record<string, unknown> | null;
  selection_rationale?: string | null;
}

export interface HandoffPackageList {
  items: HandoffPackage[];
  total: number;
}

export interface ListPackagesParams {
  /** Server caps this at 100. */
  limit?: number;
  offset?: number;
  source_type?: SourceType;
}

export interface UseCaseHandoffCreate {
  use_case_id: string;
  title?: string;
  instructions?: string;
}

export interface ProjectHandoffCreate {
  project_id: string;
  title?: string;
  instructions?: string;
}

export interface StandaloneHandoffCreate {
  title?: string;
  input_text?: string;
  files?: File[];
}

/* --------------------------------------------------------------------------
 * SSE stream
 * ----------------------------------------------------------------------- */

export interface HandoffStatusPayload {
  status?: PackageStatus;
  message?: string;
  step?: string;
}

export interface HandoffSelectionPayload {
  skills: string[];
  agents: string[];
  rationale: string;
}

export interface HandoffTokenPayload {
  content: string;
}

export interface HandoffPackagedPayload {
  zip_bytes: number;
}

export interface HandoffCompletePayload {
  status?: PackageStatus;
  zip_bytes?: number;
}

export interface HandoffErrorPayload {
  message: string;
}

export type CoWorkStreamEvent =
  | { event: 'status'; data: HandoffStatusPayload }
  | { event: 'selection'; data: HandoffSelectionPayload }
  | { event: 'token'; data: HandoffTokenPayload }
  | { event: 'packaged'; data: HandoffPackagedPayload }
  | { event: 'complete'; data: HandoffCompletePayload }
  | { event: 'error'; data: HandoffErrorPayload };

export interface StreamGenerationOptions {
  onEvent: (event: CoWorkStreamEvent) => void;
  signal?: AbortSignal;
}

/**
 * Parse one complete `\n\n`-delimited SSE frame. Returns `null` for comments,
 * heartbeats and unknown event names so callers can ignore them safely.
 */
const parseSseFrame = (frame: string): CoWorkStreamEvent | null => {
  let eventName = '';
  const dataLines: string[] = [];

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '');
    if (line === '' || line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    }
  }

  if (!eventName || dataLines.length === 0) return null;

  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join('\n'));
  } catch {
    console.error('CoWork SSE: unparsable data payload for event', eventName);
    return null;
  }

  switch (eventName) {
    case 'status':
      return { event: 'status', data: payload as HandoffStatusPayload };
    case 'selection':
      return { event: 'selection', data: payload as HandoffSelectionPayload };
    case 'token':
      return { event: 'token', data: payload as HandoffTokenPayload };
    case 'packaged':
      return { event: 'packaged', data: payload as HandoffPackagedPayload };
    case 'complete':
      return { event: 'complete', data: payload as HandoffCompletePayload };
    case 'error':
      return { event: 'error', data: payload as HandoffErrorPayload };
    default:
      return null;
  }
};

/**
 * Extract the download filename from a Content-Disposition header.
 * RFC 5987: prefer `filename*` (URL-encoded UTF-8), fall back to `filename=`.
 */
const parseContentDispositionFilename = (headers: unknown, fallback: string): string => {
  const bag = (headers ?? {}) as Record<string, unknown>;
  const cd = (bag['content-disposition'] || bag['Content-Disposition'] || '') as string;
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return fallback;
    }
  }
  if (asciiMatch?.[1]) return asciiMatch[1];
  return fallback;
};

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const coworkService = {
  /** Catalog of skills and agents the selector can pick from. */
  getLibrary: async (): Promise<LibraryCatalog> => {
    const response = await apiClient.get(`${apiBaseURL}/library`);
    return response.data as LibraryCatalog;
  },

  /** Paginated handoff history for the current organisation (newest first). */
  listPackages: async (params: ListPackagesParams = {}): Promise<HandoffPackageList> => {
    const response = await apiClient.get(`${apiBaseURL}/packages`, {
      params: {
        limit: params.limit ?? 20,
        offset: params.offset ?? 0,
        source_type: params.source_type,
      },
    });
    return response.data as HandoffPackageList;
  },

  getPackage: async (id: string): Promise<HandoffPackageDetail> => {
    const response = await apiClient.get(`${apiBaseURL}/packages/${id}`);
    return response.data as HandoffPackageDetail;
  },

  /** Create a handoff package seeded from an existing use case. */
  createFromUseCase: async (payload: UseCaseHandoffCreate): Promise<HandoffPackageDetail> => {
    const response = await apiClient.post(`${apiBaseURL}/packages/use-case`, payload);
    return response.data as HandoffPackageDetail;
  },

  /**
   * Create a handoff package from a project the user can read: its record, latest
   * analysis and current-version files become the package sources.
   */
  createFromProject: async (payload: ProjectHandoffCreate): Promise<HandoffPackageDetail> => {
    const response = await apiClient.post(`${apiBaseURL}/packages/project`, payload);
    return response.data as HandoffPackageDetail;
  },

  /** Create a standalone handoff package from free-form text and/or files. */
  createStandalone: async (payload: StandaloneHandoffCreate): Promise<HandoffPackageDetail> => {
    const formData = new FormData();
    if (payload.title) formData.append('title', payload.title);
    if (payload.input_text) formData.append('input_text', payload.input_text);
    (payload.files ?? []).forEach((file) => formData.append('files', file));

    const response = await apiClient.post(`${apiBaseURL}/packages/standalone`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data as HandoffPackageDetail;
  },

  deletePackage: async (id: string): Promise<void> => {
    await apiClient.delete(`${apiBaseURL}/packages/${id}`);
  },

  /** Fetch the generated zip and save it through an anchor click. */
  downloadPackage: async (id: string, fallbackFilename?: string): Promise<void> => {
    const response = await apiClient.get(`${apiBaseURL}/packages/${id}/download`, {
      responseType: 'blob',
    });
    const filename = parseContentDispositionFilename(
      response.headers,
      fallbackFilename ?? `cowork-handoff-${id}.zip`
    );
    triggerBlobDownload(response.data as Blob, filename);
  },

  /**
   * Open the generation SSE stream. The endpoint is a GET but auth is a bearer
   * token, so `EventSource` can't be used — we read the `ReadableStream`
   * ourselves and buffer across network chunks, only handing complete
   * `\n\n`-delimited frames to the parser (a token split across two chunks
   * would otherwise corrupt the brief).
   */
  streamGeneration: async (id: string, options: StreamGenerationOptions): Promise<void> => {
    const { onEvent, signal } = options;
    const { accessToken, impersonatedUser } = useAuthStore.getState();

    try {
      const response = await fetch(
        `${getApiBaseUrl()}${apiBaseURL}/packages/${id}/generate/stream`,
        {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            ...(impersonatedUser?.id ? { 'X-Impersonate-User-Id': impersonatedUser.id } : {}),
          },
          signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const drain = (flushTail: boolean): void => {
        let separatorIndex = buffer.indexOf('\n\n');
        while (separatorIndex !== -1) {
          const frame = buffer.slice(0, separatorIndex);
          buffer = buffer.slice(separatorIndex + 2);
          const parsed = parseSseFrame(frame);
          if (parsed) onEvent(parsed);
          separatorIndex = buffer.indexOf('\n\n');
        }
        if (flushTail && buffer.trim() !== '') {
          const parsed = parseSseFrame(buffer);
          if (parsed) onEvent(parsed);
          buffer = '';
        }
      };

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        drain(false);
      }

      buffer += decoder.decode();
      drain(true);
    } catch (error) {
      // A user-triggered abort is not an error.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (error instanceof Error && error.name === 'AbortError') return;
      throw error;
    }
  },
};

export default coworkService;
